'use client'

import { ExtendedEvent } from '../types'
import {
  getGameName,
  getGameImageUrl,
  formatTimeRange,
  calculateAvailableSlots,
  getCapacityStatusColors,
} from '../utils/event-processor'
import GameImage from './GameImage'
import AccessibleEvent from './AccessibleEvent'

interface DayViewEventProps {
  event: ExtendedEvent
  onClick?: () => void
  onKeyboardNavigation?: (direction: 'up' | 'down' | 'left' | 'right') => void
}

/**
 * Component for rendering events in day view (maximum space)
 * Shows more details than TimeGridEvent since there's more space in day view
 */
export function DayViewEvent({ event, onClick, onKeyboardNavigation }: DayViewEventProps) {
  const { extendedProps } = event
  const gameName = getGameName(event)
  const gameImageUrl = getGameImageUrl(event)

  // Format the time range
  const startTime = event.start
    ? typeof event.start === 'string'
      ? event.start
      : event.start instanceof Date
      ? event.start.toISOString()
      : null
    : null

  const endTime = event.end
    ? typeof event.end === 'string'
      ? event.end
      : event.end instanceof Date
      ? event.end.toISOString()
      : null
    : null

  const timeRange = formatTimeRange(startTime, endTime)

  // Calculate player slots
  const maxPlayers = extendedProps?.maxPlayers || 0
  const currentPlayers = extendedProps?.participantCount || 0
  const availableSlots = calculateAvailableSlots(maxPlayers, currentPlayers)
  const { bg: capacityBgColor, text: capacityTextColor } = getCapacityStatusColors(
    currentPlayers,
    maxPlayers
  )

  // Get location if available
  const location = extendedProps?.location || ''

  const renderContent = () => (
    <div className="fc-event-custom-content flex">
      <div className="fc-event-thumbnail-container mr-3 flex-shrink-0">
        {gameImageUrl ? (
          <GameImage
            src={gameImageUrl || '/placeholder-game.png'}
            alt=""
            className="fc-event-thumbnail w-16 h-16 object-cover rounded-md"
            width={64}
            height={64}
            fallbackSrc="/placeholder-game.png"
            aria-hidden="true"
          />
        ) : (
          <div
            className="fc-event-thumbnail w-16 h-16 flex items-center justify-center bg-gray-700 rounded-md"
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="fc-event-title font-medium text-base truncate">{event.title}</div>
        {gameName && (
          <div className="fc-event-game-name text-sm opacity-80 truncate">{gameName}</div>
        )}
        <div className="fc-event-info text-sm">
          <span className="fc-event-time opacity-80">{timeRange}</span>
        </div>
        {location && (
          <div className="fc-event-location text-xs opacity-70 truncate mt-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3 inline mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {location}
          </div>
        )}
        {maxPlayers > 0 && (
          <div className="fc-event-slots text-xs mt-2">
            <span
              className="inline-flex px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: capacityBgColor,
                color: capacityTextColor,
              }}
            >
              {availableSlots > 0
                ? `${availableSlots} seat${availableSlots !== 1 ? 's' : ''} available`
                : 'Full'}
            </span>
            <span className="ml-2 text-xs opacity-70">
              {currentPlayers}/{maxPlayers} players
            </span>
          </div>
        )}
        {extendedProps?.host && (
          <div className="fc-event-host text-xs opacity-70 mt-1">
            <span className="font-medium">Host:</span>{' '}
            {extendedProps.host.full_name || extendedProps.host.username}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <AccessibleEvent event={event} onClick={onClick} onKeyboardNavigation={onKeyboardNavigation}>
      {renderContent()}
    </AccessibleEvent>
  )
}
